import axios from 'axios';
import httpClient from '@/src/lib/api/http/client';
import { ApplicationError } from '@/src/shared/errors';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { DCreateSellerAccount, DGetSellerAccount } from '../data/dtos/account';
import { DGetCustomerContact } from '../data/dtos/contact';
import {
  DCreateCustomerConversation,
  DGetCustomerConversations,
  DGetSellerConversations,
} from '../data/dtos/conversation';
import {
  DUpdateCustomerLastSeen,
  DUpdateSellerLastSeen,
} from '../data/dtos/last-seen';
import {
  DCreateCustomerMessage,
  DCreateSellerMessage,
  DGetCustomerMessages,
  DGetSellerMessages,
} from '../data/dtos/message';
import {
  mapChatAccountSchemaToChatAccountModel,
  mapChatContactSchemaToChatContactModel,
  mapChatConversationSchemaToChatConversationModel,
  mapChatMessageSchemaToChatMessageModel,
} from '../data/mappers';
import {
  SCreateCustomerConversationRequest,
  TCreateCustomerConversationRequest,
} from '../data/schemas/api/requests/create-customer-conversation';
import {
  SChatAccountResponse,
  TChatAccountResponse,
} from '../data/schemas/api/responses/chat-account';
import {
  SChatContactResponse,
  TChatContactResponse,
} from '../data/schemas/api/responses/chat-contact';
import {
  SChatConversationResponse,
  SChatConversationsResponse,
  TChatConversationResponse,
  TChatConversationsResponse,
} from '../data/schemas/api/responses/chat-conversation';
import {
  SChatMessageResponse,
  SChatMessagesResponse,
  TChatMessageResponse,
  TChatMessagesResponse,
} from '../data/schemas/api/responses/chat-message';
import {
  SUpdateLastSeenResponse,
  TUpdateLastSeenResponse,
} from '../data/schemas/api/responses/last-seen';
import {
  CreateAccountFailedError,
  GetAccountFailedError,
  GetContactFailedError,
  GetConversationsFailedError,
  GetMessagesFailedError,
  SendMessageFailedError,
} from '../errors';

export const marketplaceChatService = {
  createSellerChatAccount: async ({ branchId }: DCreateSellerAccount) => {
    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/marketplace/branches/${branchId}`
      );
      const responseSchema = safeParseZodObject<TChatAccountResponse>(
        SChatAccountResponse,
        response.data
      );
      return mapChatAccountSchemaToChatAccountModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new CreateAccountFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  createCustomerConversation: async ({
    branchId,
    userId,
  }: DCreateCustomerConversation) => {
    const requestBody = {
      user_id: userId,
    };
    const request = safeParseZodObject<TCreateCustomerConversationRequest>(
      SCreateCustomerConversationRequest,
      requestBody
    );

    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/marketplace/branches/${branchId}/conversations`,
        request
      );
      const responseSchema = safeParseZodObject<TChatConversationResponse>(
        SChatConversationResponse,
        response.data
      );
      return mapChatConversationSchemaToChatConversationModel(
        responseSchema.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetConversationsFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getCustomerContact: async ({
    userId,
    conversationId,
  }: DGetCustomerContact) => {
    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/marketplace/users/${userId}/conversations/${conversationId}/contact`
      );
      const responseSchema = safeParseZodObject<TChatContactResponse>(
        SChatContactResponse,
        response.data
      );
      return mapChatContactSchemaToChatContactModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetContactFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getSellerChatAccount: async ({ branchId }: DGetSellerAccount) => {
    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/marketplace/branches/${branchId}/account`
      );
      const responseSchema = safeParseZodObject<TChatAccountResponse>(
        SChatAccountResponse,
        response.data
      );
      return mapChatAccountSchemaToChatAccountModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetAccountFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  sendCustomerMessage: async ({
    userId,
    conversationId,
    content,
    attachments,
  }: DCreateCustomerMessage) => {
    const request = new FormData();
    if (content) {
      request.append('content', content);
    }
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        request.append('attachments', file);
      });
    }

    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/marketplace/users/${userId}/conversations/${conversationId}/messages`,
        request
      );
      const responseSchema = safeParseZodObject<TChatMessageResponse>(
        SChatMessageResponse,
        response.data
      );
      return mapChatMessageSchemaToChatMessageModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new SendMessageFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  sendSellerMessage: async ({
    branchId,
    conversationId,
    content,
    attachments,
  }: DCreateSellerMessage) => {
    const request = new FormData();
    if (content) {
      request.append('content', content);
    }
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        request.append('attachments', file);
      });
    }

    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/marketplace/branches/${branchId}/conversations/${conversationId}/messages`,
        request
      );
      const responseSchema = safeParseZodObject<TChatMessageResponse>(
        SChatMessageResponse,
        response.data
      );
      return mapChatMessageSchemaToChatMessageModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new SendMessageFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getCustomerConversations: async ({
    userId,
    page,
    branchId,
  }: DGetCustomerConversations) => {
    const params = {
      page,
      ...(branchId && { branch_id: branchId }),
    };

    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/marketplace/users/${userId}/conversations`,
        { params }
      );
      const responseSchema = safeParseZodObject<TChatConversationsResponse>(
        SChatConversationsResponse,
        response.data
      );
      return responseSchema.data.map((conversation) => {
        return mapChatConversationSchemaToChatConversationModel(conversation);
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetConversationsFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getSellerConversations: async ({
    branchId,
    page,
  }: DGetSellerConversations) => {
    const params = {
      page,
    };

    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/marketplace/branches/${branchId}/conversations`,
        { params }
      );
      const responseSchema = safeParseZodObject<TChatConversationsResponse>(
        SChatConversationsResponse,
        response.data
      );
      return responseSchema.data.map((conversation) => {
        return mapChatConversationSchemaToChatConversationModel(conversation);
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetConversationsFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getCustomerMessages: async ({
    userId,
    conversationId,
    lastMessageId,
  }: DGetCustomerMessages) => {
    const params = {
      ...(lastMessageId && { last_message_id: lastMessageId }),
    };

    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/marketplace/users/${userId}/conversations/${conversationId}/messages`,
        { params }
      );
      const responseSchema = safeParseZodObject<TChatMessagesResponse>(
        SChatMessagesResponse,
        response.data
      );
      return responseSchema.data.map((message) => {
        return mapChatMessageSchemaToChatMessageModel(message);
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetMessagesFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getSellerMessages: async ({
    branchId,
    conversationId,
    lastMessageId,
  }: DGetSellerMessages) => {
    const params = {
      ...(lastMessageId && { last_message_id: lastMessageId }),
    };

    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/marketplace/branches/${branchId}/conversations/${conversationId}/messages`,
        { params }
      );
      const responseSchema = safeParseZodObject<TChatMessagesResponse>(
        SChatMessagesResponse,
        response.data
      );
      return responseSchema.data.map((message) => {
        return mapChatMessageSchemaToChatMessageModel(message);
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetMessagesFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  updateSellerLastSeen: async ({
    branchId,
    conversationId,
  }: DUpdateSellerLastSeen) => {
    try {
      const response = await httpClient.healthyoneMarketplace.patch(
        `/chat/marketplace/branches/${branchId}/conversations/${conversationId}/last-seen`
      );
      const responseSchema = safeParseZodObject<TUpdateLastSeenResponse>(
        SUpdateLastSeenResponse,
        response.data
      );
      return responseSchema.data.last_seen;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetMessagesFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  updateCustomerLastSeen: async ({
    userId,
    conversationId,
  }: DUpdateCustomerLastSeen) => {
    try {
      const response = await httpClient.healthyoneMarketplace.patch(
        `/chat/marketplace/users/${userId}/conversations/${conversationId}/last-seen`
      );
      const responseSchema = safeParseZodObject<TUpdateLastSeenResponse>(
        SUpdateLastSeenResponse,
        response.data
      );
      return responseSchema.data.last_seen;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetMessagesFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },
};
